import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from "chart.js";
import type { ChartOptions, Plugin } from "chart.js";
import { Scatter } from "react-chartjs-2";
import type {
  DoubleCrossing,
  DoubleStats
} from "../../types/doubleStats";
import {
  ACTUAL_COLOR,
  GRID_COLOR,
  PROJECTION_COLOR,
  TICK_COLOR,
  dateAxis,
  fmtFullDate
} from "./chartHelpers";

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend);

type Props = {
  crossings: DoubleCrossing[];
  currentDeaths: number;
  latestSnapshotAt: string;
  projection: DoubleStats["projection"];
};

const toPoints = (path: DoubleStats["projection"]["expectedPath"]) =>
  path.map((p) => ({ x: new Date(p.at).getTime(), y: p.deaths / 1e9 }));

const RoadChart = ({
  crossings,
  currentDeaths,
  latestSnapshotAt,
  projection
}: Props) => {
  const actual = [
    ...crossings.map((c) => ({
      x: new Date(c.crossedAt).getTime(),
      y: c.milestone / 1e9
    })),
    { x: new Date(latestSnapshotAt).getTime(), y: currentDeaths / 1e9 }
  ];
  const expected = toPoints(projection.expectedPath);
  const fast = toPoints(projection.fastPath);
  const slow = toPoints(projection.slowPath);
  const target = projection.target / 1e9;

  // Shaded range between the fast and slow cases, drawn as one polygon.
  const band: Plugin = {
    id: "paceBand",
    beforeDatasetsDraw(chart) {
      if (!fast.length || !slow.length) return;
      const { ctx, scales } = chart;
      const { x, y } = scales;
      ctx.save();
      ctx.beginPath();
      [...fast, ...[...slow].reverse()].forEach((p, i) => {
        const px = x.getPixelForValue(p.x);
        const py = y.getPixelForValue(p.y);
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.closePath();
      ctx.fillStyle = "rgba(224, 185, 61, 0.16)";
      ctx.fill();
      ctx.restore();
    }
  };

  const lastSlow = slow[slow.length - 1]?.x;
  const options: ChartOptions<"scatter"> = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1.7,
    scales: {
      x: {
        ...dateAxis(12),
        // A little room past the slowest case
        max: lastSlow ? lastSlow + 90 * 86400000 : undefined
      },
      y: {
        suggestedMax: target,
        ticks: {
          color: TICK_COLOR,
          callback: (v) => `${Number(v).toFixed(2)}B`
        },
        grid: { color: GRID_COLOR }
      }
    },
    plugins: {
      legend: { display: true, labels: { color: TICK_COLOR } },
      tooltip: {
        callbacks: {
          title: (items) => fmtFullDate(items[0].parsed.x ?? 0),
          label: (item) =>
            `${item.dataset.label}: ${(item.parsed.y ?? 0).toFixed(3)}B`
        }
      }
    }
  };

  const flat =
    projection.flatAt && actual.length
      ? [
          actual[actual.length - 1],
          { x: new Date(projection.flatAt).getTime(), y: target }
        ]
      : [];

  return (
    <Scatter
      options={options}
      plugins={[band]}
      data={{
        datasets: [
          {
            label: "Actual",
            data: actual,
            showLine: true,
            borderColor: ACTUAL_COLOR,
            backgroundColor: ACTUAL_COLOR,
            borderWidth: 2.5,
            pointRadius: 0,
            pointHitRadius: 6
          },
          {
            label: projection.usesSeasonality
              ? "Projection (seasonal)"
              : "Projection",
            data: expected,
            showLine: true,
            borderColor: PROJECTION_COLOR,
            backgroundColor: PROJECTION_COLOR,
            borderWidth: 2.5,
            borderDash: [7, 5],
            pointRadius: 0,
            pointHitRadius: 6
          },
          {
            label: "Flat average pace",
            data: flat,
            showLine: true,
            borderColor: "rgba(199, 238, 255, 0.5)",
            borderDash: [2, 4],
            borderWidth: 1.5,
            pointRadius: 0,
            pointHitRadius: 6
          }
        ]
      }}
    />
  );
};

export default RoadChart;
